import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/redux-toolkit/store';
import { GetHelpCenterTasks, GetHelpCenterTopics } from '@/apis/helpCenterAPI';
import { useToken } from '@/common/helper';
import { canUseAiInsightsChat } from '@/common/utils/utils';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { trackEvent as trackBeacon } from '@/hooks';
import { BeaconEventType } from '@/common/enum';
import {
  BrowseByTopic,
  CommonTasks,
  HelpArticleView,
  HelpAssistantView,
  HelpCategoryView,
  HelpPanelHeader,
  HelpSearch,
  NewInZoomlogi,
  StillStuck,
} from '../components';
import { stillStuckData } from '@/common/static_data';
import {
  useHelpCurrentPage,
  useHelpDeepLink,
  useHelpEvent,
  useHelpNavigationStack,
} from '../hooks';
import type {
  HelpCenterProps,
  HelpCenterTasksData,
  HelpCenterTopicsData,
  HelpView,
} from '../types';
import styles from '../AppHelpCenter.module.scss';
import { ConfigProvider, Flex, message } from 'antd';

// The panel is z-index 2000 (AppHelpCenter.module.scss) but antd popups
// default to 1100, so tooltips/modals opened from inside Help would sit under
// it. Every antd popup rendered from this tree stacks off this base instead.
const HELP_POPUP_Z_INDEX_BASE = 2100;

// PRD: panel pushes page content at >=1100px and becomes an overlay below it.
const OVERLAY_BREAKPOINT = 1100;

const VIEW_TITLES: Record<HelpView, string> = {
  home: 'Help center',
  category: 'Category',
  article: 'Article',
  assistant: 'AI Assistant',
};

// Drawer shell only (Phase 1). Home/Search/Category/Article/Assistant
// content lands in components/ during later phases of
// frontend/guides/HELP_CENTER_PLAN.md. This panel stays position: fixed in
// both modes, always docked flush to the true right edge; the push-mode
// content shift is a `.content .main.help-center-push` margin owned by
// src/layout/index.tsx (DashboardLayout) + Layout.scss, since this
// component has no reach into that sibling element.
const HelpCenterPanel: React.FC<HelpCenterProps> = ({
  open,
  dataModalOpen,
  onClose,
  onOpen,
  openSource,
  onAskInsightChat,
}) => {
  const { width } = useBreakpoints();
  const isOverlay = width < OVERLAY_BREAKPOINT;
  const { current, canGoBack, back, goHome, push } = useHelpNavigationStack();
  const trackEvent = useHelpEvent();
  // A shared ?help=article:{id} link opens Help on that article.
  useHelpDeepLink(open, onOpen, (id, referrer) =>
    push({ view: 'article', params: referrer ? { id, referrer } : { id } })
  );

  // Telemetry: one help_open per open (a remount restore has no source).
  // The activity beacon gets help_opened too, as the old Help dropdown sent.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current && openSource) {
      trackEvent({ event: 'help_open', props: { source: openSource } });
      trackBeacon(BeaconEventType.HelpOpened);
    } else if (!open && wasOpenRef.current) {
      trackEvent({ event: 'help_close' });
    }
    wasOpenRef.current = open;
    // Runs when Help opens or closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const routePage = useHelpCurrentPage();
  const menuItems = useSelector((state: RootState) => state.menu.items);
  // Data is a modal (the menu's non-route item), not a page; while it's open,
  // Common tasks show Data's tasks instead of the page underneath.
  const dataPage = menuItems?.find((item) => item.is_route === false)?.name;
  const currentPage = dataModalOpen && dataPage ? dataPage : routePage;
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const { token, decodedUserToken, getPreviewState } = useToken();
  // PRD: the AI master toggle (Settings > AI controls) gates every assistant entry.
  const aiEnabled = !!decodedUserToken()?.ai_enable;
  // PRD: the Insight Chat hand-off follows the user's Insight Chat access.
  const insightChatEnabled = canUseAiInsightsChat(
    decodedUserToken(),
    getPreviewState().isPreview
  );
  const [topicsData, setTopicsData] = useState<HelpCenterTopicsData | null>(
    null
  );
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [seeAll, setSeeAll] = useState(false);
  // Remembers which params the tasks belong to, so revisiting Home skips the call.
  const [tasks, setTasks] = useState<{
    params: string;
    data: HelpCenterTasksData | null;
  } | null>(null);
  const [tasksLoading, setTasksLoading] = useState(true);

  // Fetched here, not in CommonTasks, so leaving Home doesn't drop the data.
  // Calls again only when the page or See all changes.
  useEffect(() => {
    const page =
      currentPage?.toLowerCase() == 'shipment' ? 'Shipments' : currentPage;
    const params = seeAll ? `page=all&current=${page}` : `page=${page}`;
    if (!open || tasks?.params === params) return undefined;

    let cancelled = false;
    setTasksLoading(true);
    GetHelpCenterTasks(token, params)
      .then((res) => {
        if (!cancelled) {
          setTasks({ params, data: res?.data?.data?.data ?? null });
        }
      })
      .catch(() => {
        if (!cancelled) setTasks(null);
      })
      .finally(() => {
        if (!cancelled) setTasksLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `tasks` is read only to skip a repeat call, not to trigger one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token, currentPage, seeAll]);

  // One topics call feeds both Browse by topic and New in Zoomlogi; loaded once.
  useEffect(() => {
    if (!open || topicsData) return undefined;
    let cancelled = false;
    setTopicsLoading(true);
    GetHelpCenterTopics(token)
      .then((res) => {
        if (!cancelled) setTopicsData(res?.data?.data?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setTopicsData(null);
      })
      .finally(() => {
        if (!cancelled) setTopicsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `topicsData` is read only to skip a repeat call, not to trigger one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused.current?.focus?.();
    };
  }, [open, onClose]);

  // PRD: Help stays on the right edge and pushes the shipment detail panel left instead of overlapping it.
  useEffect(() => {
    document.body.classList.toggle('help-center-open', open);
    return () => document.body.classList.remove('help-center-open');
  }, [open]);

  const headerTitle = current.params?.name ?? VIEW_TITLES[current.view];

  // PRD: copies the current page URL with help=article:{id}, which opens Help on this article.
  const copyArticleLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('help', `article:${current.params?.id ?? ''}`);
    navigator.clipboard
      .writeText(url.toString())
      .then(() => {
        message.success('Link copied');
        trackEvent({
          event: 'help_article_link_copy',
          props: { article_id: current.params?.id ?? '' },
        });
      })
      .catch(() => message.error('Could not copy link'));
  };

  return (
    <ConfigProvider
      theme={{ token: { zIndexPopupBase: HELP_POPUP_Z_INDEX_BASE } }}
    >
      {open && isOverlay && (
        <div
          className={styles.backdrop}
          data-testid='help-center-backdrop'
          onClick={onClose}
        />
      )}

      <div
        ref={panelRef}
        className={`${styles.panel} ${open ? styles.panelOpen : ''}`}
        role='dialog'
        aria-modal={isOverlay}
        aria-label='Help'
        aria-hidden={!open}
        // PRD 5.1: content stays mounted while closed so reopening keeps the
        // view and scroll; inert blocks focus and clicks while it's hidden.
        // React 18 types lack `inert`, so it goes in as a plain attribute.
        {...(!open && { inert: '' })}
        tabIndex={-1}
        data-testid='help-center-panel'
      >
        <HelpPanelHeader
          title={headerTitle}
          canGoBack={canGoBack}
          showCopyLink={current.view === 'article'}
          onBack={back}
          onHome={goHome}
          onCopyLink={copyArticleLink}
          onClose={onClose}
        />

        {/* Assistant sits outside .body so its composer can pin to the bottom. */}
        {current.view === 'assistant' ? (
          <HelpAssistantView
            key={current.params?.question ?? ''}
            initialQuestion={current.params?.question}
            articleContextId={current.params?.article_id}
            insightChatEnabled={insightChatEnabled}
            onOpenArticle={(article) =>
              push({
                view: 'article',
                params: { id: article.id, name: article.title },
              })
            }
            onAskInsightChat={onAskInsightChat}
          />
        ) : (
          <div className={styles.body} data-testid='help-center-body'>
            <Flex vertical gap={30}>
              {current.view === 'category' && (
                <HelpCategoryView
                  categoryId={current.params?.id ?? ''}
                  onOpenArticle={(article) =>
                    push({
                      view: 'article',
                      params: {
                        id: article.id,
                        name: article.title,
                        referrer: 'category',
                      },
                    })
                  }
                />
              )}

              {current.view === 'article' && (
                <HelpArticleView
                  articleId={current.params?.id ?? ''}
                  referrer={current.params?.referrer}
                  onOpenArticle={(article, referrer) =>
                    push({
                      view: 'article',
                      params: { id: article.id, name: article.title, referrer },
                    })
                  }
                  onOpenCategory={(category) =>
                    push({ view: 'category', params: category })
                  }
                  // The article id goes along as the assistant's context.
                  onAskAssistant={(question) =>
                    push({
                      view: 'assistant',
                      params: {
                        question,
                        article_id: current.params?.id ?? '',
                      },
                    })
                  }
                  aiEnabled={aiEnabled}
                />
              )}

              {current.view === 'home' && (
                <>
                  <HelpSearch
                    onOpenArticle={(result) =>
                      push({
                        view: 'article',
                        params: {
                          id: result.id,
                          name: result.title,
                          referrer: 'search',
                        },
                      })
                    }
                    onAskAssistant={(query) =>
                      push({
                        view: 'assistant',
                        params: { question: query },
                      })
                    }
                    onAskInsightChat={onAskInsightChat}
                    aiEnabled={aiEnabled}
                    insightChatEnabled={insightChatEnabled}
                  />
                  <CommonTasks
                    data={tasks?.data ?? null}
                    loading={tasksLoading}
                    seeAll={seeAll}
                    onToggleSeeAll={() => setSeeAll((prev) => !prev)}
                    onSelect={(task) =>
                      push({
                        view: 'article',
                        params: {
                          id: task.id,
                          name: task.title,
                          referrer: 'task',
                        },
                      })
                    }
                  />
                  <BrowseByTopic
                    topics={topicsData?.topics ?? []}
                    loading={topicsLoading}
                    onSelect={(topic) =>
                      push({
                        view: 'category',
                        params: { id: topic.id, name: topic.name },
                      })
                    }
                  />
                  <NewInZoomlogi
                    whatsNew={topicsData?.whats_new}
                    loading={topicsLoading}
                    onSelect={(item) =>
                      push({
                        view: 'article',
                        params: { id: item.id, name: item.title },
                      })
                    }
                    onViewAll={() =>
                      push({
                        view: 'category',
                        params: {
                          id: 'new-in-zoomlogi',
                          name: 'New in Zoomlogi',
                        },
                      })
                    }
                  />
                  {/* Still stuck section is ready */}
                  <StillStuck
                    items={stillStuckData}
                    onOpenAssistant={() => push({ view: 'assistant' })}
                    aiEnabled={aiEnabled}
                  />
                </>
              )}
            </Flex>
          </div>
        )}
      </div>
    </ConfigProvider>
  );
};

export default HelpCenterPanel;
